import { useMemo } from 'react';
import { SERIES_COLORS } from '../../CommonComponents/OPL/charts/charts.constants';
import {
  formatCurrency as formatNumberCurrency,
  formatPercentage as formatNumberPercentage,
} from '../../CommonUtils/formatNumber';
import { AsyncComponentHookResult } from '../../Types/ComponentHookResult';
import { usePositionsDetailsQuery } from '../api/usePositionsDetailsQuery';
import { useOptions } from './useOptions';

const formatCurrency = value =>
  formatNumberCurrency(value, { decimalPlaces: 2, thousandSeparated: true });

const formatPercentage = value => formatNumberPercentage(value, { decimalPlaces: 2 });

const getTabIndex = (
  selectedTabIndex: number | null,
  navigationIndex: number,
  defaultIndex: number,
) => {
  if (selectedTabIndex !== null) {
    return selectedTabIndex;
  }
  if (navigationIndex >= 0) {
    return navigationIndex;
  }
  if (defaultIndex >= 0) {
    return defaultIndex;
  }

  return 0;
};

export interface Cell {
  text: string | number;
  type?: 'currency' | 'number' | 'percent';
  fontWeight?: 400 | 600;
}

type ChildRow = {
  cells: Cell[];
};

export type Row = {
  id: string;
  cells: Cell[];
  isTotal?: boolean;
  childRows?: ChildRow[];
  color?: string | null;
};

export interface TableData {
  headers: Cell[];
  rows: Row[];
}

export interface ChartPoint {
  label: string;
  value: number;
  color: string;
}

export interface PositionsData {
  tabList: string[];
  tabIndex: number;
  tableData: TableData | null;
  chartData: ChartPoint[] | null;
  showCostBasis: boolean;
}

interface UsePositionsParams {
  clientId: string;
  date: string;
  accountId: string;
  groupId: string;
  allocationType: string;
  selectedTabIndex: number | null;
}

export const usePositionsDetails = ({
  clientId,
  date,
  accountId,
  groupId,
  allocationType,
  selectedTabIndex,
}: UsePositionsParams): AsyncComponentHookResult<PositionsData> => {
  const queryInfo = usePositionsDetailsQuery({
    clientId,
    date,
    accountId,
    groupId,
  });

  const {
    transactionsSettings: { showCostBasis },
  } = useOptions();

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const { defaultGroup, groups } = queryInfo.data;

    // Empty state
    if (groups.length === 0) {
      return {
        tabList: [],
        tabIndex: 0,
        tableData: null,
        chartData: null,
        showCostBasis,
      };
    }

    const tabList = groups.map(({ name }) => name);

    const navigationIndex = tabList.findIndex(
      name => name.toLowerCase() === decodeURIComponent(allocationType || '').toLowerCase(),
    );

    const defaultIndex = tabList.findIndex(name => name === defaultGroup);

    const tabIndex = getTabIndex(selectedTabIndex, navigationIndex, defaultIndex);

    const group = groups[tabIndex];

    const tableData = {} as TableData;

    tableData.headers = [
      {
        text: 'Name',
      },
      {
        text: 'Ticker',
      },
      {
        text: 'Units',
      },
      {
        text: 'Price',
      },
      {
        text: 'Market Value',
      },
      {
        text: 'Allocation',
      },
    ];

    tableData.rows = [
      ...group.rows.map(({ id, positions, color, marketValue, name, percent }, index) => ({
        id,

        cells: [
          { text: name },
          { text: '' },
          { text: '' },
          { text: '' },
          { text: marketValue, type: 'currency' },
          { text: percent, type: 'percent' },
        ] as Cell[],

        color: color || SERIES_COLORS[index % SERIES_COLORS.length],

        childRows: [
          ...positions.map(childRow => ({
            cells: [
              { text: childRow.name },
              { text: childRow.ticker },
              { text: childRow.units ?? '', type: 'number' },
              { text: childRow.price ?? '', type: 'currency' },
              { text: childRow.marketValue ?? '', type: 'currency' },
              { text: childRow.percent ?? '', type: 'percent' },
            ] as Cell[],
          })),
        ],
      })),

      {
        id: 'total',
        cells: [
          { text: 'Total' },
          { text: '' },
          { text: '' },
          { text: '' },
          { text: formatCurrency(group.rowsTotal.marketValue) },
          { text: formatPercentage(group.rowsTotal.percent / 100) },
        ],
        isTotal: true,
      },
    ];

    const chartData = group.rows.map(({ id, name, percent, color }, index) => ({
      id,
      label: name,
      value: percent / 100,
      color: color || SERIES_COLORS[index % SERIES_COLORS.length],
    }));

    return {
      tableData,
      tabList,
      tabIndex,
      chartData,
      showCostBasis,
    };
  }, [queryInfo.data, selectedTabIndex, showCostBasis, allocationType]);

  return {
    ...queryInfo,
    data,
  };
};
